<template>
  <!-- Navigation Bar (sticky on desktop) -->
  <nav class="main-navigation hide-on-print" :style="navStyle" :class="{scrolled: isScrolled, 'is-home': isHomePage, 'hero-visible': isHomePage && !isScrolled}">
    <!-- Logo compact pour les autres pages et mode scrollé -->
    <div class="nav-logo" v-if="!isHomePage || isScrolled || isMiam">
      <router-link :to="{name: 'home'}" class="flex items-center">
        <img v-if="headerLogo" :src="headerLogo" class="nav-logo-img" :alt="`${chefCustomerConfig.headerTitle} - ${chefCustomerConfig.headerBaseline}`" :title="`${chefCustomerConfig.headerTitle} - ${chefCustomerConfig.headerBaseline}`">
        <div class="nav-text">
          <div class="nav-title" v-html="headerTitle"></div>
          <div class="nav-subtitle">{{ chefCustomerConfig.headerBaseline }}</div>
        </div>
      </router-link>
    </div>

    <!-- Menu burger (mobile) -->
    <div class="nav-burger block lg:hidden">
      <button 
        @click="toggleMenu" 
        class="flex items-center px-3 py-2 border rounded text-teal-lighter border-teal-light hover:text-white hover:border-white"
        >
        <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg" style="width: 20px;height: 20px;">
          <title>Menu</title><path d="M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z"/>
        </svg>
      </button>
    </div>

    <!-- Menu links -->
    <div 
      class="nav-menu lg:flex lg:items-center"
      :class="{hidden: !menuOpened}"
      @click="onMenuClicked"
      >
      <div class="nav-menu-links text-sm">
        <router-link 
        v-if="customerWithOrder"
        :to="{name: 'currentMenu'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4"
        v-tooltip="'Voir le menu du moment et passer commande'"
        >
          {{hasSeveralsMenus ? headerViewMenusText : headerViewMenuText }}
        </router-link>

        <router-link 
        v-if="hasHowToOrder && customerWithOrder"
        :to="{name: 'howToOrder'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4"
        >
          Comment ça marche
        </router-link>

        <router-link 
        v-if="hasPrestations"
        :to="{name: 'prestations'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4"
        >
          Prestations
        </router-link>
        
        <router-link 
        :to="{name: 'contact'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Contact
        </router-link>

        <router-link 
        v-if="hasAbout"
        :to="{name: 'about'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          A propos
        </router-link>

        <router-link 
        v-if="hasNews"
        :to="{name: 'news'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Actualités
        </router-link>
        
        <router-link 
        v-if="isAuthenticated"
        :to="{name: 'allMenus'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Tous les menus
        </router-link>

        <router-link 
        v-if="isAuthenticated"
        :to="{name: 'admin'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Administration
        </router-link>

        <router-link 
        v-if="isAuthenticated"
        :to="{name: 'logout'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Se déconnecter
        </router-link>

        <router-link 
        v-if="!isAuthenticated && alreadyLoogedOnceTimeAgo"
        :to="{name: 'login'}"
        class="block mt-4 lg:inline-block lg:mt-0 text-teal-lighter mr-4">
          Se connecter
        </router-link>

        <!-- Réseaux sociaux mobile uniquement -->
        <div class="nav-social-mobile lg:hidden">
          <div class="social-mobile-title">Réseaux sociaux</div>
          <a v-if="chefCustomerConfig.instaPageUrl" :href="chefCustomerConfig.instaPageUrl" title="Visiter ma page Instagram" target="_blank">
            <i class="pi pi-instagram"></i>
            <span>Instagram</span>
          </a>
          <a v-if="chefCustomerConfig.facebookPageUrl" :href="chefCustomerConfig.facebookPageUrl" title="Visiter ma page Facebook" target="_blank">
            <i class="pi pi-facebook"></i>
            <span>Facebook</span>
          </a>
          <a href="#" @click.stop.prevent="subscribeToNewsletter" title="Recevoir les informations par mail" class="js-subscribeToNewsletter">
            <i class="pi pi-at"></i>
            <span>Newsletter</span>
          </a>
          <a v-if="chefCustomerConfig.whatsappJoinLink" href="#" @click.stop.prevent="joinWhatsApp" title="Rejoindre le groupe WhatsApp" class="js-whatsApp">
            <i class="pi pi-whatsapp"></i>
            <span>WhatsApp</span>
          </a>
        </div>
      </div>

      <!-- Réseaux sociaux dans la nav -->
      <div class="nav-social-networks lg:flex-grow">
        <a v-if="chefCustomerConfig.instaPageUrl" :href="chefCustomerConfig.instaPageUrl"  title="Visiter ma page Instagram" target="_blank">
          <i class="pi pi-instagram"></i>
        </a>
        <a v-if="chefCustomerConfig.facebookPageUrl" :href="chefCustomerConfig.facebookPageUrl" title="Visiter ma page Facebook" target="_blank">
          <i class="pi pi-facebook"></i>
        </a>
        <a href="#" @click.stop.prevent="subscribeToNewsletter" title="Recevoir les informations par mail" class="js-subscribeToNewsletter">
          <i class="pi pi-at"></i>
        </a>
        <a v-if="chefCustomerConfig.whatsappJoinLink" href="#" @click.stop.prevent="joinWhatsApp" title="Rejoindre le groupe WhatsApp" class="js-whatsApp">
          <i class="pi pi-whatsapp"></i>
        </a>
      </div>
    </div>
  </nav>

  <!-- Hero Logo Section (image + titre + baseline) -->
  <!-- Wrapper avec min-height pour éviter l'effet yoyo au scroll -->
  <div class="hero-area hide-on-print" v-if="isHomePage && isFaimDeSaison" :style="heroAreaStyle">
    <div class="header-hero" ref="heroEl" :class="{scrolled: isScrolled, 'is-home': isHomePage}">
      <router-link :to="{name: 'home'}" class="hero-content">
        <img v-if="headerLogoSquare" :src="headerLogoSquare" class="hero-logo-img" alt="Logo">
        <div class="hero-text">
          <div class="hero-title" v-html="headerTitle"></div>
          <div class="hero-subtitle">{{ chefCustomerConfig.headerBaseline }}</div>
        </div>
      </router-link>
      
      <!-- Réseaux sociaux mobile hero (visible uniquement sur mobile en mode hero) -->
      <div class="hero-social-mobile lg:hidden" v-if="!isScrolled">
        <a v-if="chefCustomerConfig.instaPageUrl" :href="chefCustomerConfig.instaPageUrl" title="Visiter ma page Instagram" target="_blank">
          <i class="pi pi-instagram"></i>
        </a>
        <a v-if="chefCustomerConfig.facebookPageUrl" :href="chefCustomerConfig.facebookPageUrl" title="Visiter ma page Facebook" target="_blank">
          <i class="pi pi-facebook"></i>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { useAdminDataStore } from '../stores/admin'
import { useFrontDataStore } from '../stores/front'
import { useRoute } from 'vue-router'
import setConfigClass from '../misc/setConfigClass'

export default {
  name: "About",
  setup() {
    const adminDataStore = useAdminDataStore();
    const frontDataStore = useFrontDataStore();
    const route = useRoute();

    const { chefCustomerConfig } = storeToRefs(frontDataStore);
    if(document.location.hostname == 'localhost') {
      chefCustomerConfig.value.instaPageUrl = 'https://www.instagram.com/faim_de_saison/';
    } 

    // Détection du scroll et de la page home
    const isScrolled = ref(false);
    const isHomePage = computed(() => route.name === 'home');

    // Ref pour le hero et sa hauteur initiale (anti-yoyo)
    const heroEl = ref(null);
    const heroFullHeight = ref(278);

    const heroAreaStyle = computed(() => {
      if (heroFullHeight.value > 0) {
        return { minHeight: heroFullHeight.value + 'px' };
      }
      return {};
    });

    const handleScroll = () => {
      isScrolled.value = window.scrollY > 100;
    };
    const getHeight = (tryLeft) => {
      if(tryLeft == 0) {
        return;
      }
      
      if (heroEl.value) {
        heroFullHeight.value = heroEl.value.offsetHeight;
      }else{
        setTimeout(() => getHeight(tryLeft - 1), 300);
      }
    };

    onMounted(() => {
      handleScroll();
      window.addEventListener('scroll', handleScroll);
      
      // Capturer la hauteur initiale du hero après le rendu
      nextTick(() => {
        getHeight(10);
      });
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    const customerWithOrder = computed(() => chefCustomerConfig.value.withOrder);

    const headerViewMenuText = computed(() => chefCustomerConfig.value.headerViewMenuText);
    const headerViewMenusText = computed(() => chefCustomerConfig.value.headerViewMenusText);

    const howToOrderEnabled = computed(() => chefCustomerConfig.value.howToOrderEnabled);

    //const custumerOrderOnHome = computed(() => chefCustomerConfig.value.withOrder && chefCustomerConfig.value.orderOnHome);
    const custumerOrderOnOtherPage = computed(() => chefCustomerConfig.value.withOrder && !chefCustomerConfig.value.orderOnHome);
    setConfigClass(chefCustomerConfig.value);

    const subscribeToNewsletter = () => {
      frontDataStore.displayRegisterToNewsletterPopin();
    };

    const bgColor = process.env.VUE_APP_HEADER_COLOR;
    const navStyle = bgColor ? `background-color: ${bgColor};` : '';
    

    const menuOpened = ref(false);
    const toggleMenu = () => {
      menuOpened.value = !menuOpened.value;
    };

    const onMenuClicked = () => {
      menuOpened.value = false;
    };

    // Fermer le burger lors d'un changement de route
    watch(() => route.path, () => {
      menuOpened.value = false;
    });

    const { homeMenus } = storeToRefs(frontDataStore);
    const hasSeveralsMenus = computed(() => homeMenus.value.length >= 2);

    const alreadyLoogedOnceTimeAgo = !!(localStorage.getItem('loginUsername'));

    const { isAuthenticated } = storeToRefs(adminDataStore);

    const joinWhatsApp = () => {
      //document.location='ht'+'tps://c'+'hat.w'+'hat'+'sapp.com/H4K5J'+'osJ29XEeTtKXiNSWa';
      if(chefCustomerConfig.value.whatsappJoinLink) {
        document.location = chefCustomerConfig.value.whatsappJoinLink;
      }
    };

    const escapeHtml = (text) => {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, (m) => map[m]);
    };

    const headerTitle = computed(() => {
      let title = escapeHtml(chefCustomerConfig.value.headerTitle || 'Faim de saison');
      if(title == 'Faim de saison') {
        return 'Faim <span class="faim2s-de">de</span> saison';
      }
      return title;
    });

    const headerLogo = computed(() => {
      const skinName = process.env.VUE_APP_SKIN_NAME;
      // Mapping des noms de skin vers les noms de fichiers logo
      const logoMapping = {
        'faim2s': 'logo-couleur-horizontal-no-baseline-400.png',
        'miam': '',
        'cheforga': ''
      };
      const logoSrc = logoMapping[skinName] || skinName;
      
      try {
        return logoSrc ? require(`@/assets/${logoSrc}`) : null;
      } catch (e) {
        // Si le logo n'existe pas pour ce skin, retourner null
        return null;
      }
    });

    const isMiam = computed(() => process.env.VUE_APP_SKIN_NAME === 'miam');
    const isFaimDeSaison = computed(() => process.env.VUE_APP_SKIN_NAME === 'faim2s');

    const headerLogoSquare = computed(() => {
      const skinName = process.env.VUE_APP_SKIN_NAME;
      // Mapping des noms de skin vers les noms de fichiers logo carré
      const logoMapping = {
        'faim2s': 'logo-couleur-horizontal.png',
        'miam': '',
        'cheforga': ''
      };
      const logoSrc = logoMapping[skinName] || skinName;
      
      try {
        return logoSrc ? require(`@/assets/${logoSrc}`) : null;
      } catch (e) {
        // Si le logo n'existe pas pour ce skin, retourner null
        return null;
      }
    });

    const { pages } = storeToRefs(frontDataStore);
    const hasHowToOrder = computed(() => howToOrderEnabled.value && (pages.value['howToOrder'] || {}).descriptionGeneratedHtml != '');
    const hasPrestations = computed(() => ( (pages.value['prestations'] || {}).descriptionGeneratedHtml != '') || ((pages.value['prestationstakeaway'] || {}).descriptionGeneratedHtml != ''));
    const hasAbout = computed(() => (pages.value['about'] || {}).descriptionGeneratedHtml != '');
    const hasNews = computed(() => (pages.value['news'] || {}).descriptionGeneratedHtml != '');

    return {
      alreadyLoogedOnceTimeAgo,
      chefCustomerConfig,
      customerWithOrder,
      headerLogo,
      headerLogoSquare,
      headerTitle,
      custumerOrderOnOtherPage,
      hasHowToOrder,
      hasPrestations,
      hasAbout,
      hasNews,
      hasSeveralsMenus,
      headerViewMenuText,
      headerViewMenusText,
      heroAreaStyle,
      heroEl,
      isAuthenticated,
      isHomePage,
      isFaimDeSaison,
      isMiam,
      isScrolled,
      joinWhatsApp,
      menuOpened,
      navStyle,
      onMenuClicked,
      subscribeToNewsletter,
      toggleMenu,
    };
  }
};
</script>